import { entryWindowTriggers } from '@/features/backtest/schemas/compound'
import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import {
  ENTRY_WINDOW_HANDLE,
  ENTRY_WINDOW_PARAMS,
  entryWindowParameters,
} from '@/features/backtest/schemas/node-schemas/entry-window-schema'
import { materializeNode } from '@/features/backtest/graph-engine/materialize'
import { isPassthroughType, resolvePassthroughUpstream } from './inline-passthroughs'
import { freshId, isProxyEdge, readParams, type LooseEdge, type LooseNode } from './graph-lowering'

export { ENTRY_WINDOW_HANDLE, ENTRY_WINDOW_PARAMS }

/**
 * The entry window, expanded for the engine.
 *
 * Simple Trade and Entry Rules carry an entry window (schemas/node-schemas/
 * entry-window-schema.ts): typed From / Until times, or a wire into their
 * Window socket. The engine has no such thing. It has Time Signal and Merge,
 * so before a graph is sent the window becomes exactly what a user would have
 * built by hand: the trigger wire goes through a new Merge (AND) whose other
 * input is the window, a generated Time Signal or the wired source. A wire
 * wins over the fields.
 *
 * The trigger is the wire that fires the entry:
 *   - Simple Trade: its `entry` socket.
 *   - Entry Rules inside an Advanced Trade: the trade's `trigger` socket
 *     (there, Entry Rules' own `signal` is the order price).
 *   - A standalone Entry Rules: its `signal` socket, when a true/false value
 *     arrives there. A price on that socket is not a trigger.
 *
 * `gates` is a list so day-of-week can join the window as a second input of
 * the same Merge.
 */

export type EntryWindowGate<E extends LooseEdge = LooseEdge> =
  | { kind: 'wire'; edge: E }
  | { kind: 'fields'; start: string; end: string }

export interface EntryWindowPlan<E extends LooseEdge = LooseEdge> {
  entryId: string
  /** What limits the entry. Each one is ANDed with the trigger. */
  gates: EntryWindowGate<E>[]
  /** The wire that fires the entry, or null when the window has nothing to limit. */
  trigger: E | null
}

/** Entry node types that carry a window: their schemas declare the facet (schemas/compound.ts). */
export const isEntryWindowNodeType = (type: string | undefined): boolean =>
  !!type && entryWindowTriggers().has(type)

/** The socket a standalone entry of this type hears its trigger on (its facet declaration). */
const triggerSocket = (type: string): string => entryWindowTriggers().get(type) ?? ''

const DEFAULTS = Object.fromEntries(entryWindowParameters.map((p) => [p.name, p.default]))

const timeOr = (value: unknown, fallback: unknown): string =>
  typeof value === 'string' && value !== '' ? value : String(fallback)

/** Whether a wire carries a true/false value, seen through reroutes and subnet pass-throughs. */
function carriesBoolean(edge: LooseEdge, nodes: LooseNode[], edges: LooseEdge[]): boolean {
  const seen = new Set<string>()
  let current: LooseEdge | undefined = edge
  while (current && !seen.has(current.id)) {
    seen.add(current.id)
    const from: string = current.source
    const direct = nodes.find((n) => n.id === from)
    const source: { id: string; type: string; sourceHandle: string | null | undefined } | null =
      isPassthroughType(direct?.type)
        ? resolvePassthroughUpstream(from, nodes, edges)
        : { id: from, type: direct?.type ?? '', sourceHandle: current.sourceHandle }
    if (!source) return false
    const schema = UniversalNodeRegistry.getSchemaResolved(source.type)
    const output =
      schema?.outputs.find((o) => o.id === source.sourceHandle) ??
      (schema?.outputs.length === 1 ? schema.outputs[0] : undefined)
    if (!output) return false
    if (!output.passthrough) return output.type === 'boolean'
    // Bar Offset and friends forward what they are given: look at what that is.
    current = edges.find((e) => e.target === source.id)
  }
  return false
}

/** The nodes an output reaches, walking through reroutes and subnet pass-throughs. */
function consumers(
  nodeId: string,
  nodes: LooseNode[],
  edges: LooseEdge[],
): Array<{ node: LooseNode; edge: LooseEdge }> {
  const out: Array<{ node: LooseNode; edge: LooseEdge }> = []
  const seen = new Set<string>()
  const walk = (from: string) => {
    if (seen.has(from)) return
    seen.add(from)
    for (const edge of edges) {
      if (edge.source !== from) continue
      const node = nodes.find((n) => n.id === edge.target)
      if (!node) continue
      if (isPassthroughType(node.type)) walk(node.id)
      else out.push({ node, edge })
    }
  }
  walk(nodeId)
  return out
}

function findTrigger<E extends LooseEdge>(
  entry: LooseNode,
  nodes: LooseNode[],
  edges: E[],
): E | null {
  const into = (nodeId: string, handle: string) =>
    edges.find((e) => e.target === nodeId && e.targetHandle === handle) ?? null

  if (entry.type === 'simpleTrade') return into(entry.id, triggerSocket('simpleTrade'))

  const trade = consumers(entry.id, nodes, edges).find(
    ({ node, edge }) => node.type === 'trade' && edge.targetHandle === 'entry',
  )
  if (trade) return into(trade.node.id, 'trigger')

  const signal = into(entry.id, triggerSocket('entryRules'))
  return signal && carriesBoolean(signal, nodes, edges) ? signal : null
}

/** Every entry that uses a window, with what limits it and the wire it limits. */
export function planEntryWindows<N extends LooseNode, E extends LooseEdge>(
  nodes: N[],
  edges: E[],
): EntryWindowPlan<E>[] {
  const canonical = edges.filter((e) => !isProxyEdge(e))
  const plans: EntryWindowPlan<E>[] = []
  for (const node of nodes) {
    if (!isEntryWindowNodeType(node.type)) continue
    const wire = canonical.find(
      (e) => e.target === node.id && e.targetHandle === ENTRY_WINDOW_HANDLE,
    )
    const params = readParams(node)
    const gates: EntryWindowGate<E>[] = wire
      ? [{ kind: 'wire', edge: wire }]
      : params.entryWindow === true
        ? [
            {
              kind: 'fields',
              start: timeOr(params.entryWindowStart, DEFAULTS.entryWindowStart),
              end: timeOr(params.entryWindowEnd, DEFAULTS.entryWindowEnd),
            },
          ]
        : []
    if (gates.length === 0) continue
    plans.push({ entryId: node.id, gates, trigger: findTrigger(node, nodes, canonical) })
  }
  return plans
}

function requireSchema(type: string) {
  const schema = UniversalNodeRegistry.getSchema(type)
  // Without it the window would quietly vanish from the run. Fail loudly instead.
  if (!schema) throw new Error(`Entry window: the "${type}" node schema is not registered`)
  return schema
}

/**
 * Expand every entry window into a Time Signal and a Merge (AND) on the raw
 * editor graph, before serialization, so the generated nodes are serialized
 * exactly like hand-built ones. Window wires are always removed: the engine
 * has no such socket. Pure; returns the input arrays when nothing uses a window.
 */
export function lowerEntryWindows<N extends LooseNode, E extends LooseEdge>(
  nodes: N[],
  edges: E[],
): { nodes: N[]; edges: E[]; owners: Map<string, string> } {
  // Each generated Time Signal and Merge, to the entry node whose window it is.
  const owners = new Map<string, string>()
  const plans = planEntryWindows(nodes, edges)
  if (plans.length === 0) return { nodes, edges, owners }

  const taken = new Set(nodes.map((n) => n.id))
  const added: N[] = []
  const dropped = new Set<string>()
  const addedEdges: E[] = []
  // Inputs whose denormalized `source` now points at the wrong node; cleared so
  // serialization derives it from the new wire (backfillInputSourcesFromEdges).
  const rewired = new Map<string, Set<string>>()

  const wire = (
    source: string,
    sourceHandle: string | null | undefined,
    target: string,
    targetHandle: string,
  ) =>
    ({
      id: `e-${source}-${sourceHandle ?? ''}-${target}-${targetHandle}`,
      source,
      sourceHandle,
      target,
      targetHandle,
    }) as unknown as E

  for (const plan of plans) {
    for (const gate of plan.gates) if (gate.kind === 'wire') dropped.add(gate.edge.id)
    const trigger = plan.trigger
    if (!trigger) continue

    const target = nodes.find((n) => n.id === trigger.target)
    const at = target?.position ?? { x: 0, y: 0 }
    const place = <T extends N>(node: T, dx: number, dy: number): T =>
      ({
        ...node,
        position: { x: at.x + dx, y: at.y + dy },
        ...(target?.parentId ? { parentId: target.parentId } : {}),
      }) as T

    const sources: Array<{ source: string; sourceHandle: string | null | undefined }> = []
    for (const gate of plan.gates) {
      if (gate.kind === 'wire') {
        sources.push({ source: gate.edge.source, sourceHandle: gate.edge.sourceHandle })
        continue
      }
      const id = freshId(`${plan.entryId}-window`, taken)
      owners.set(id, plan.entryId)
      const { node } = materializeNode(
        { id, type: 'timeSignal', params: { startTime: gate.start, endTime: gate.end } },
        { schemaById: new Map([[id, requireSchema('timeSignal')]]), position: at },
      )
      added.push(place(node as unknown as N, -260, 140))
      sources.push({ source: id, sourceHandle: 'true' })
    }

    const mergeId = freshId(`${plan.entryId}-window-and`, taken)
    owners.set(mergeId, plan.entryId)
    const { node: merge } = materializeNode(
      { id: mergeId, type: 'merge', params: { operator: 'AND' } },
      { schemaById: new Map([[mergeId, requireSchema('merge')]]), position: at },
    )
    const inputs = [trigger, ...sources].map((_, i) => ({
      name: `input_${i + 1}`,
      inputType: 'node',
      source: null,
      value: null,
      enabled: true,
    }))
    added.push(place({ ...merge, data: { ...merge.data, inputs } } as unknown as N, -130, 0))

    dropped.add(trigger.id)
    addedEdges.push(
      wire(trigger.source, trigger.sourceHandle, mergeId, 'input_1'),
      ...sources.map((s, i) => wire(s.source, s.sourceHandle, mergeId, `input_${i + 2}`)),
      wire(mergeId, 'result', trigger.target, trigger.targetHandle ?? ''),
    )
    const handles = rewired.get(trigger.target) ?? new Set<string>()
    handles.add(trigger.targetHandle ?? '')
    rewired.set(trigger.target, handles)
  }

  const nextNodes = nodes.map((node) => {
    const handles = rewired.get(node.id)
    const data = node.data as { inputs?: unknown } | undefined
    if (!handles || !Array.isArray(data?.inputs)) return node
    const inputs = (data.inputs as Array<Record<string, unknown>>).map((input) =>
      handles.has(input.name as string) ? { ...input, source: null } : input,
    )
    return { ...node, data: { ...data, inputs } }
  })

  return {
    nodes: [...nextNodes, ...added],
    edges: [
      // Canonical wires only: toEngineView drops a collapsed subnet's proxy
      // wires before any lowering runs.
      ...edges.filter((e) => !dropped.has(e.id)),
      ...addedEdges,
    ],
    owners,
  }
}

/**
 * Take the window off every Simple Trade and Entry Rules: its parameters and
 * the Window input entry. For the engine payload this runs AFTER
 * serialization, which re-adds Entry Rules' schema defaults. The saved graph
 * keeps them; only the engine (and the code printer) is spared fields that
 * `lowerEntryWindows` has already turned into nodes.
 */
export function stripEntryWindowFields<N extends { type?: unknown; data?: unknown }, E>(graph: {
  nodes: N[]
  edges: E[]
}): { nodes: N[]; edges: E[] } {
  const names = new Set<string>(ENTRY_WINDOW_PARAMS)
  return {
    nodes: graph.nodes.map((node) => {
      if (!isEntryWindowNodeType(node.type as string | undefined)) return node
      const data = node.data as Record<string, unknown> | undefined
      if (!data) return node
      const next: Record<string, unknown> = { ...data }
      if (Array.isArray(data.parameters))
        next.parameters = (data.parameters as Array<Record<string, unknown>>).filter(
          (p) => !names.has(p.name as string),
        )
      else if (data.parameters && typeof data.parameters === 'object')
        next.parameters = Object.fromEntries(
          Object.entries(data.parameters).filter(([name]) => !names.has(name)),
        )
      if (Array.isArray(data.inputs))
        next.inputs = (data.inputs as Array<Record<string, unknown>>).filter(
          (i) => i.name !== ENTRY_WINDOW_HANDLE,
        )
      return { ...node, data: next }
    }),
    edges: graph.edges,
  }
}
